import React from 'react';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Tooltip } from '@newcore/ui/tooltips';
import { Text } from '@newcore/ui/typography';
import { AppIcon } from '@/components/common/app-icon';
import { FavoriteButton } from '@/components/ui/buttons/favorite-button';
import { SvgIcon } from '@/components/ui/icons';
import { SsoType } from '@/enums/sso-type';
import type { UserApplication } from '@/types/api.types';
import { getApplicationIconUrl } from '@/utils/icon-utils';

interface ApplicationTileProps {
  application: UserApplication;
  onClick: (application: UserApplication) => void;
  isLoading?: boolean;
}

export const ApplicationTile: React.FC<ApplicationTileProps> = React.memo(
  ({ application, onClick, isLoading = false }) => {
    const isSskProtected =
      application.sso_type === SsoType.SAML_SSK ||
      application.sso_type === SsoType.OIDC_SSK;
    const isFavorite = application.is_favorite;

    const handleClick = () => {
      if (!isLoading) {
        onClick(application);
      }
    };

    return (
      <div className='group relative flex w-16 shrink-0 flex-col items-center gap-1.5'>
        <button
          type='button'
          onClick={handleClick}
          disabled={isLoading}
          title={application.app_name}
          data-testid={`application-tile-${application.app_id}`}
          className='flex flex-col items-center gap-1.5'
        >
          <span className='relative block size-16 shrink-0'>
            <span
              className={cn(
                'flex size-full items-center justify-center overflow-hidden rounded-2xl border border-solid border-secondary bg-primary transition-all duration-150',
                'group-hover:border-primary group-hover:shadow-xs'
              )}
            >
              <AppIcon
                iconUrl={getApplicationIconUrl(
                  application.icon_path,
                  application.app_name
                )}
                appName={application.app_name}
                size={Sizes.LG}
              />
            </span>
            {isSskProtected && (
              <Tooltip content='This app is protected by Secure Split Key — signing requires your company NewCore browser extension'>
                <SvgIcon
                  name='ssk'
                  size={Sizes.MD}
                  aria-label='Protected with Secure Split Key'
                  data-testid='ssk-badge'
                  className='absolute -bottom-1 -right-1'
                />
              </Tooltip>
            )}
          </span>

          <Text
            size={TextSizes.XS}
            weight={FontWeights.MEDIUM}
            className={cn(
              'line-clamp-2 w-24 break-words text-center transition-colors',
              isLoading
                ? 'text-tertiary'
                : 'text-quaternary group-hover:text-secondary'
            )}
          >
            {application.app_name}
          </Text>
        </button>

        <div
          className={cn(
            'absolute right-1 top-1 transition-opacity',
            !isFavorite &&
              'md:invisible md:group-hover:visible md:group-focus-within:visible'
          )}
        >
          <FavoriteButton
            applicationId={application.app_id}
            isFavorite={isFavorite}
          />
        </div>
      </div>
    );
  }
);

ApplicationTile.displayName = 'ApplicationTile';
